import type { PainelResumoDto, TurmaDto } from '@sheepcount/shared';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PageLoading } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { TurmaFilterSelect, turmaFiltroValido, useTurmaFilter } from '@/lib/turma-filter';
import { cn } from '@/lib/utils';

const GRID_COLS = 'grid grid-cols-[2fr_1.3fr_0.9fr_0.9fr_0.9fr_1fr] min-w-[680px]';

export function PainelPage() {
  const { turmaId: turmaFiltro } = useTurmaFilter();
  const [resumo, setResumo] = useState<PainelResumoDto | null>(null);
  const [turmas, setTurmas] = useState<TurmaDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [onlyRisco, setOnlyRisco] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Turmas primeiro: o filtro salvo só vale se a turma ainda é visível.
      const turmasData = await api<TurmaDto[]>('/turmas');
      const filtro = turmaFiltroValido(turmaFiltro, turmasData);
      const resumoData = await api<PainelResumoDto>(
        `/painel/resumo${filtro !== '' ? `?turmaId=${filtro}` : ''}`,
      );
      setTurmas(turmasData);
      setResumo(resumoData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o painel');
    }
  }, [turmaFiltro]);

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-[22px] font-bold tracking-[-0.01em]">Painel Admin</h1>
      <TurmaFilterSelect turmas={turmas} />
    </div>
  );

  if (error) {
    return (
      <div>
        {header}
        <div className="flex flex-col items-start gap-3">
          <div className="text-[13px] text-danger">{error}</div>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (resumo === null) {
    return (
      <div>
        {header}
        <PageLoading />
      </div>
    );
  }

  const lista = onlyRisco ? resumo.alunos.filter((a) => a.emRisco) : resumo.alunos;

  return (
    <div>
      {header}

      <div className="mb-[22px] grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        <Card>
          <div className="mb-1.5 text-xs text-muted">Alunos</div>
          <div className="text-2xl font-extrabold">{resumo.totalAlunos}</div>
        </Card>
        <Card>
          <div className="mb-1.5 text-xs text-muted">Turmas</div>
          <div className="text-2xl font-extrabold">{resumo.totalTurmas}</div>
        </Card>
        <Card>
          <div className="mb-1.5 text-xs text-muted">Encontros</div>
          <div className="text-2xl font-extrabold">{resumo.totalEncontros}</div>
        </Card>
        <Card className="border-danger-border bg-danger-bg">
          <div className="mb-1.5 text-xs text-danger">Em risco de reprovação</div>
          <div className="text-2xl font-extrabold text-danger">{resumo.totalEmRisco}</div>
        </Card>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
        <div className="text-[15px] font-bold">Alunos</div>
        <button
          type="button"
          onClick={() => setOnlyRisco((v) => !v)}
          className={cn(
            'cursor-pointer rounded-lg border px-3.5 py-2 text-[12.5px] font-semibold transition-colors',
            onlyRisco
              ? 'border-transparent bg-primary text-white'
              : 'border-input bg-surface text-fg-soft hover:bg-chip',
          )}
        >
          {onlyRisco ? 'Mostrando: em risco' : 'Mostrando: todos'}
        </button>
      </div>

      <div className="max-w-full overflow-x-auto rounded-[10px] border border-border bg-surface">
        <div
          className={cn(
            GRID_COLS,
            'border-b border-border px-4 py-3 text-[11.5px] font-bold uppercase tracking-[0.02em] text-muted',
          )}
        >
          <div>Nome</div>
          <div>Turma</div>
          <div>Presença</div>
          <div>Meta</div>
          <div>Faltas</div>
          <div>Status</div>
        </div>
        {lista.length === 0 ? (
          <div className="px-4 py-6 text-[13px] text-muted">
            {onlyRisco ? 'Nenhum aluno em risco.' : 'Nenhum aluno cadastrado.'}
          </div>
        ) : (
          lista.map((aluno) => (
            <div
              key={aluno.alunoId}
              className={cn(
                GRID_COLS,
                'items-center border-b border-[oklch(94%_0.004_90)] px-4 py-[13px] text-[13.5px] last:border-b-0',
              )}
            >
              <div className="font-semibold">{aluno.nome}</div>
              <div className="text-muted">{aluno.turmaNome}</div>
              <div className="font-semibold">{aluno.percentual}%</div>
              <div className="text-muted">{aluno.metaPresenca}%</div>
              <div className="text-muted">
                {aluno.faltas} de {aluno.totalEncontros}
              </div>
              <div>
                <Badge
                  variant={aluno.emRisco ? 'danger' : 'success'}
                  className="px-2.5 py-1 text-[11.5px] font-bold"
                >
                  {aluno.emRisco ? 'Em risco' : 'Em dia'}
                </Badge>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
